import React, { useEffect, useState, useContext } from 'react';
import { View, colors, GridColumn, Text, Button, Link } from '@fairygodboss/ui';
import window from 'global/window';
import { CommentReactionBar } from './CommentReactionBar';
import { CommentInputSection } from './CommentsHeader';
import {
    CommunityContext,
    PageContext,
} from '../../../../../../contexts/ContextCreator';
import { EmitMetric } from '../../../../../Analytics/VisibilityPixel/VisibilityPixel';
import { getFormattedTimestamp } from '../../../../../Utilities/TimeFunctions';
import { NoComments } from './NoComments';
import { VipWingSVG } from './VipWingSVG';
import { AuthWrapper } from '../../../../../../pages/HomePage_v2/sections/components/AuthWrapper';
import ResponsivePicture from '../../../../../Elements/ResponsivePicture/ResponsivePicture';
import { StyledModerationMenu } from '../../ContentBody/ModerationMenu';

const CommentHeader = ({
    author,
    contentDate,
    hash,
    handleOnEditClick,
    showingEditBox,
    isDeleted,
    didEdit,
    isVip,
    isCommunityDiscussionType,
}) => {
    if (isDeleted) {
        return (
            <Text
                fontWeight="400"
                fontSize="14px"
                lineHeight="24px"
                color={colors.neutral[60]}
            >
                Comment deleted by user
            </Text>
        );
    }
    const image = author?.image;
    const username = author?.username;
    const { publicUrl } = author;

    const breakpoints = {
        mobile: {
            width: 48,
            transforms: {
                quality: 'auto',
                crop: 'scale',
                width: '48px',
                format: 'auto',
            },
        },
    };

    const formattedTimestamp = getFormattedTimestamp(contentDate);
    return (
        <View
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            pb="16px"
            css={{
                img: {
                    height: '48px',
                    width: '48px',
                },
            }}
        >
            <View
                display="flex"
                flex="1"
                {...(isCommunityDiscussionType && {
                    itemProp: 'author',
                    itemScope: 'itemscope',
                    itemType: 'https://schema.org/Person',
                })}
            >
                <View
                    as={publicUrl ? 'a' : 'div'}
                    href={publicUrl}
                    target="_self"
                    position="relative"
                    itemProp="url"
                >
                    <View
                        mr="16px"
                        borderRadius="50%"
                        overflow="hidden"
                        height="48px"
                        width="48px"
                    >
                        <ResponsivePicture
                            cloudinaryID={
                                image?.src || '/v1/avatar_black_160_vtg7lw.svg'
                            }
                            alt={image?.alt}
                            disableHeight
                            breakpoints={breakpoints}
                        />
                    </View>
                    {isVip ? (
                        <View
                            position="absolute"
                            top="34px"
                            left="34px"
                            id="usr-picture"
                            css={{
                                svg: {
                                    height: '16px !important',
                                    width: '16px !important',
                                },
                            }}
                        >
                            <VipWingSVG />
                        </View>
                    ) : null}
                </View>
                <View
                    display="flex"
                    flexWrap="wrap"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <View display="flex">
                        <View
                            display="flex"
                            css={{
                                p: {
                                    wordBreak: 'break-word',
                                    marginRight: '12px',
                                },
                            }}
                        >
                            <Text
                                as="p"
                                fontWeight="700"
                                margin="0"
                                textAlign="left"
                                variant="labelMd"
                                letterSpacing="-0.4px"
                                lineHeight="18px"
                                fontSize="16px"
                                color={colors.neutral[100]}
                                {...(isCommunityDiscussionType && {
                                    itemProp: 'name',
                                })}
                            >
                                {username}
                            </Text>
                        </View>
                    </View>
                    <View>
                        <Text
                            color={colors.neutral[60]}
                            variant="labelMd"
                            fontSize="12px"
                            lineHeight="14px"
                            {...(isCommunityDiscussionType && {
                                itemProp: 'datePublished',
                                content: contentDate,
                            })}
                        >
                            {formattedTimestamp} {didEdit ? '(Edited)' : ''}
                        </Text>
                    </View>
                </View>
            </View>
            <StyledModerationMenu
                hash={hash}
                location="single"
                handleOnEditClick={handleOnEditClick}
                editboxShowing={showingEditBox}
                canDelete
                pr="0px"
                mr={{ _: '-16px', md: '-12px' }}
            />
        </View>
    );
};

const Comment = ({
    content,
    level,
    setActiveComment,
    activeComment,
    contentUrl,
    isCommunityDiscussionType,
}) => {
    const { hash } = content;
    const { getContent } = useContext(CommunityContext);
    const { isLoggedIn } = useContext(PageContext);
    const _content = getContent(hash);
    const { comments, commentCount, parentContentHash, type } = _content;
    const [expandComment, setExpandComment] = useState(false);

    useEffect(() => {
        if (activeComment === hash) {
            setExpandComment(true);
        } else if (expandComment) {
            setExpandComment(false);
        }
    }, [activeComment, hash, expandComment]);

    const [showEditBox, setShowEditBox] = useState(false);

    useEffect(() => {
        if (showEditBox && activeComment) {
            setShowEditBox(false);
        }
    }, [showEditBox, activeComment]);

    let url = null;
    if (level > 3 && commentCount > 0) {
        const parentContent = getContent(parentContentHash);
        url = parentContent?.group?.groupUrl
            ? `${parentContent?.group.groupUrl}${parentContent?.contentUrl}/${hash}`
            : `${parentContent.contentUrl}/${hash}`;
    }
    const isDeleted = _content.moderationOption?.status?.type === 'delete';
    const didEdit = _content.moderationOption?.status?.type === 'edit';

    const EmitEditMetric = () => {
        const postMetricObject = {
            content_edited: 1,
            community_content_key: `community-discussion-${hash}`,
            community_sort_type: 'default',
        };
        EmitMetric(postMetricObject);
    };

    return (
        <View
            borderTop="1px solid #C4CBD8"
            pt="16px"
            ml={{
                _: level === 1 ? '0px' : `16px`,
                sm: level === 1 ? '0px' : `24px`,
                md: level === 1 ? '0px' : `32px`,
            }}
            css={{
                a: {
                    color: `${colors.secondary[70]} !important`,
                },
                ':link': {
                    color: `${colors.secondary[70]} !important`,
                },
                '.see-more-link': {
                    color: `${colors.secondary[70]} !important`,
                    'div > svg': {
                        path: {
                            fill: colors.secondary[70],
                        },
                    },
                },
            }}
            {...(isCommunityDiscussionType && {
                itemScope: 'itemscope',
                itemType: 'https://schema.org/Comment',
                id: hash,
            })}
        >
            {isCommunityDiscussionType && (
                <meta itemProp="datePublished" content={content?.contentDate} />
            )}
            <CommentHeader
                author={content?.author}
                contentDate={content?.contentDate}
                hash={hash}
                handleOnEditClick={() => {
                    setActiveComment(null);
                    setShowEditBox(true);
                }}
                showingEditBox={showEditBox}
                isDeleted={isDeleted}
                didEdit={didEdit}
                isVip={
                    content?.isVip || content?.author?.userCommunityData?.isVip
                }
                isCommunityDiscussionType={isCommunityDiscussionType}
            />
            {!showEditBox && !isDeleted ? (
                <View
                    fontWeight="400"
                    fontSize="14px"
                    lineHeight="24px"
                    marginBottom="16px"
                    color="#353535"
                    {...(isCommunityDiscussionType && {
                        itemProp: 'text',
                    })}
                    dangerouslySetInnerHTML={{ __html: content?.comment }}
                />
            ) : null}
            {showEditBox ? (
                <CommentInputSection
                    type={type}
                    hash={hash}
                    onPost={() => {
                        EmitEditMetric();
                        setExpandComment(false);
                        setShowEditBox(false);
                        setActiveComment(null);
                    }}
                    editView
                    content={content}
                    isLoggedIn={isLoggedIn()}
                    cancelEdit={() => setShowEditBox(false)}
                />
            ) : null}
            {content?.comment && !isDeleted && !showEditBox ? (
                <CommentReactionBar
                    hash={content.hash}
                    onCommentClick={() => {
                        setActiveComment(hash);
                    }}
                />
            ) : null}
            {expandComment ? (
                <CommentInputSection
                    placeholder="Your reply..."
                    expandComment={expandComment}
                    type={type}
                    hash={hash}
                    isLoggedIn={isLoggedIn()}
                    level={level}
                    onPost={() => {
                        setActiveComment(null);
                        setExpandComment(false);
                    }}
                />
            ) : null}

            {url ? (
                <Link
                    href={url}
                    icon
                    variant="standalone"
                    py="16px"
                    target="_self"
                    className="see-more-link"
                    {...(isCommunityDiscussionType && {
                        itemProp: 'url',
                    })}
                >
                    See all replies
                </Link>
            ) : null}
            {!url && comments.length
                ? comments.map((c) => (
                      <Comment
                          content={c}
                          key={c.hash}
                          level={level + 1}
                          setActiveComment={setActiveComment}
                          activeComment={activeComment}
                          contentUrl={contentUrl}
                          isCommunityDiscussionType={isCommunityDiscussionType}
                      />
                  ))
                : null}
        </View>
    );
};

Comment.displayName = 'SingleComment';

export const CommentSection = ({
    comments,
    lastId,
    type,
    hash,
    isLoggedIn,
    contentUrl,
    numberOfCommentsToShow,
    isCommunityDiscussionType,
}) => {
    const [lastCommentId, setLastCommentId] = useState(lastId);
    const [activeComment, setActiveComment] = useState(null);
    const [commentsCollapsed, setCommentCollapsed] = useState(true);
    const [initialLoad, setInitialLoad] = useState(false);
    const [firstLoad, setFirstLoad] = useState(false);
    const [allCommentsLoaded, setAllCommentsLoaded] = useState(false);
    const { fetchMoreComments } = useContext(CommunityContext);

    useEffect(() => {
        if (comments.length > 2) {
            setCommentCollapsed(true);
        }
    }, [comments]);

    useEffect(() => {
        if (lastCommentId === -1) {
            const getInitialData = async () => {
                const _lastCommentId = await fetchMoreComments(
                    type,
                    hash,
                    null,
                    null,
                    lastCommentId,
                    false
                );
                setLastCommentId(_lastCommentId);
            };
            getInitialData();
        }
    }, [type, hash]);

    const onSeeMoreClick = async () => {
        if (!isLoggedIn) {
            return;
        }
        EmitMetric({
            community_content_key: `${type}-${hash}`,
            misc_event_type: 'see-more-comments-click',
            click: 1,
            community_content_location: 'single',
        });
        if (allCommentsLoaded) {
            setCommentCollapsed(!commentsCollapsed);
            return;
        }
        if (lastCommentId < 0 && initialLoad) {
            setCommentCollapsed(true);
            setInitialLoad(false);
            return;
        }

        if (!firstLoad) {
            setCommentCollapsed(false);
            setFirstLoad(true);
        } else {
            const _lastID = lastCommentId < 0 ? null : lastCommentId;

            const _lastCommentId = !allCommentsLoaded
                ? await fetchMoreComments(
                      type,
                      hash,
                      null,
                      null,
                      _lastID,
                      false
                  )
                : -1;

            if (_lastCommentId < 0) {
                setAllCommentsLoaded(true);
            }
            setCommentCollapsed(false);
            setInitialLoad(true);
            setLastCommentId(_lastCommentId);
        }
    };

    const getButtonText = () => {
        return !commentsCollapsed && allCommentsLoaded
            ? 'Hide comments'
            : 'See more comments';
    };

    return (
        <GridColumn
            size={{ xs: 12, sm: 12, md: 7.8, lg: 7.6 }}
            pb={{ _: '32px', md: '48px' }}
            css={{
                paddingLeft: '0px',
                paddingRight: '0px',
            }}
        >
            {comments.length ? (
                comments
                    .slice(
                        0,
                        commentsCollapsed
                            ? numberOfCommentsToShow
                            : commentsCollapsed.length
                    )
                    .map((c) => (
                        <Comment
                            setActiveComment={setActiveComment}
                            activeComment={activeComment}
                            content={c}
                            key={c.hash}
                            level={1}
                            contentUrl={contentUrl}
                            isCommunityDiscussionType={
                                isCommunityDiscussionType
                            }
                        />
                    ))
            ) : (
                <NoComments />
            )}
            {comments.length > 2 ? (
                <View
                    display="flex"
                    justifyContent="center"
                    pt="36px"
                    css={{
                        button: {
                            backgroundColor: 'transparent',
                            borderColor: colors.secondary[70],
                            ':hover': {
                                backgroundColor: colors.secondary[10],
                            },
                            span: {
                                color: colors.secondary[70],
                            },
                        },
                    }}
                >
                    <AuthWrapper
                        component={
                            <Button
                                className="see-more-button"
                                size="large"
                                variant="secondary"
                                onPress={onSeeMoreClick}
                            >
                                {getButtonText()}
                            </Button>
                        }
                        redirectUrl={window?.location?.pathname}
                        loginTrigger="comment"
                    />
                </View>
            ) : null}
        </GridColumn>
    );
};
